import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';
import type { ResolvedExperimentFallbackReason } from '@change-corgi/experiments/configs';

import type { PsfPromoteOrSharePageData } from '#src/app/pages/psf/promoteOrShare/pageContext';
import { usePsfPromoteOrSharePageData } from '#src/app/pages/psf/promoteOrShare/pageContext';
import { firedExperiments } from '#src/app/pages/psf/promoteOrShare/shared/utils';
import { treatExperiment } from '#src/app/shared/api/experiments';
import { useTrackExperimentExposure } from '#src/app/shared/hooks/tracking';

type MotivationSuppressionReason = 'flag_unresolved' | 'no_anchor';

/**
 * experiment-service's genuine resolution-failure reasons. `invalid-name` is included defensively
 * though unreachable: the experiment name falls back to a static default, never the invalid sentinel.
 * `disabled` is excluded on purpose — that's the kill-switch-off state, and also every country without
 * an FCM row (the FCM is country-scoped with no global row), so it's the normal not-targeted state
 * rather than an anomaly, matching how `useTreatWhatsappChannelExperiment` treats it as "not active":
 * firing on `disabled` would emit this event at full production traffic volume. `bot` is excluded too
 * — a declared default that fires nothing. A *successful* QA override is not a resolution failure
 * either — it sets `override: true` with `fallback` unset, so it structurally cannot match this check.
 */
const FLAG_UNRESOLVED_FALLBACK_REASONS = new Set<ResolvedExperimentFallbackReason>([
	'not-found',
	'fetch-error',
	'treat-error',
	'invalid-name',
]);

/**
 * Exactly one suppression reason per open — first match wins, in pinned precedence order:
 * flag_unresolved → no_anchor. "variant" includes a forced/QA-override variant — the variant-side
 * reason fires under an override exactly as under a real assignment.
 *
 * Note: we intentionally do *not* suppress on signature/goal data or on past-goal petitions — the
 * callout is static copy with no such data dependency (CHANGE-94929), and over-goal petitions are
 * excluded from the audience anyway (review with dandc22).
 */
function getMotivationSuppressionReason(
	experiment: PsfPromoteOrSharePageData['psfShareMotivationLayerExperiment'],
	hasAnchor: boolean,
): MotivationSuppressionReason | undefined {
	// Only a targeted country can be unresolved. experiment-service reports `fetch-error` before it
	// checks `enabled`, so during an outage every country without an FCM row would otherwise fire too.
	// A fallback resolution still carries the FCM-derived `config`.
	if (
		experiment.fallback &&
		experiment.config.enabled &&
		FLAG_UNRESOLVED_FALLBACK_REASONS.has(experiment.fallbackReason)
	) {
		return 'flag_unresolved';
	}

	if (experiment.variation !== 'variant_1') return undefined;

	// Wrapper side of the two-layer no_anchor guard (redundant by design with the presentational
	// component's render anchor): this layer owns whether the experiment shows the callout and what
	// to log, so untreated-but-exposed variant traffic is identifiable and excludable at analysis.
	// No anchor exists when neither the Copy-link special row nor the promoted Facebook CTA renders
	// (see `hasAnchor`'s derivation in ShareSlideUp/index.tsx, which mirrors
	// PetitionShareSlideUp's own divider-render condition).
	if (!hasAnchor) {
		return 'no_anchor';
	}

	return undefined;
}

/**
 * Motivation exposure: fires once per hook instance once `treated` flips true (experiment-service
 * defers treatment -- the resolved object stays `treated: false` until `treatExperiment()`
 * resolves; see the treat callback below), deduped across ShareSlideUp instances via the
 * module-level Set. Combo variant_3 can mount two instances (PageBody + post-expand
 * StripeCreditCard path); each has its own useTrackExperimentExposure mount state, so without this
 * Set a second open would double-fire $exposure.
 *
 * The key is claimed synchronously (before awaiting) so two instances racing the same tick can't
 * both pass the `has` check and both fire -- but a failed send releases the claim, so a `false`
 * result doesn't permanently lose `$exposure` for that petition for the rest of the SPA session; a
 * later mount (this instance's own retry is blocked by the caller's per-instance ref -- see
 * `attemptedRef` below) can retry.
 */
async function fireMotivationExposureIfTreated(
	experimentName: string,
	petitionId: string,
	trackExposure: () => Promise<boolean>,
): Promise<void> {
	const dedupKey = `${experimentName}:${petitionId}`;
	if (firedExperiments.has(dedupKey)) return;
	firedExperiments.add(dedupKey);
	const sent = await trackExposure();
	if (!sent) {
		firedExperiments.delete(dedupKey);
	}
}

/**
 * Suppression event, at most once per slide-up open (the caller runs once per open; a reopen is a
 * new open and may fire again). `experimentOverride` comes from the resolved experiment: true for a
 * forced/QA-override variation (the end-to-end QA path for the event shape), false for a real
 * assignment — analysis filters on experiment_override = false.
 */
function trackMotivationSuppression(
	track: ReturnType<typeof useTracking>,
	reason: MotivationSuppressionReason | undefined,
	experimentOverride: boolean,
): void {
	if (!reason) return;
	void track('psf_share_motivation_suppressed', { reason, experiment_override: experimentOverride });
}

/**
 * PSF share-slide-up motivation layer (Top-8 relaunch, CHANGE-100433): treats + fires `$exposure` on
 * slide-up open (deferred experiment-service treatment), and decides whether the commitment-nudge
 * callout should render. Country targeting lives entirely in the FCM's per-country rows.
 *
 * `hasAnchor` is owned by the caller — the callout anchors to whichever special block renders
 * above the channel grid (the Copy-link/divider row, or the promoted Facebook CTA from the
 * channel-aware experiment); it's `false` only when neither renders.
 */
export function useTreatMotivationLayerExperiment(hasAnchor: boolean): {
	showMotivationCallout: boolean;
	treatOnSlideUpOpen: () => void;
} {
	const {
		data: { petition, psfShareMotivationLayerExperiment: experiment },
	} = usePsfPromoteOrSharePageData();
	const track = useTracking();
	const utilityContext = useUtilityContext();

	const motivationSuppressionReason = getMotivationSuppressionReason(experiment, hasAnchor);
	const showMotivationCallout = experiment.variation === 'variant_1' && !motivationSuppressionReason;

	// treatExperiment (below) registers treatment server-side, but the resolved object stays
	// treated:false — override it locally once treated, otherwise useTrackExperimentExposure no-ops.
	const [treated, setTreated] = useState(false);
	const inFlightRef = useRef(false);
	const experimentForExposure = useMemo(
		() => ({ ...experiment, treated: treated || experiment.treated }),
		[experiment, treated],
	);
	const trackExposure = useTrackExperimentExposure(experimentForExposure, { resetKeys: [petition.id] });

	// `treated` only flips false→true once per instance, but `trackExposure`'s own identity churns
	// afterward (its useCallOnce dedup state update triggers a re-render), which would otherwise
	// re-run this effect a second time with `treated` still true. Guard so each instance attempts
	// at most once, regardless of how many times the effect re-fires for that single open.
	const attemptedRef = useRef(false);
	useEffect(() => {
		if (!treated || attemptedRef.current) return;
		attemptedRef.current = true;
		void fireMotivationExposureIfTreated(experiment.experimentName, petition.id, trackExposure);
	}, [treated, experiment.experimentName, petition.id, trackExposure]);

	const treatOnOpen = useCallback(async () => {
		// `treated` only flips after the async treat resolves, so also guard with an in-flight ref to
		// avoid firing a second treat if the slide-up re-opens while the first is still in flight.
		// treatExperiment bypasses the config's enabledIf, so gate on the FCM flag here too (a QA override
		// with the FCM off resolves without `fallback`). `!fallback` keeps treated == exposed:
		// useTrackExperimentExposure refuses to fire $exposure on a fallback resolution, so treating one
		// would enroll a session that can never expose.
		if (treated || inFlightRef.current || !experiment.config.enabled || experiment.fallback) return;
		inFlightRef.current = true;
		try {
			// An assignment the page query already reports as treated needs no mutation. Otherwise arm
			// $exposure only on a confirmed treat: treatExperiment resolves `false` rather than throwing
			// when the treatment did not land server-side (a later open retries).
			if (experiment.treated || (await treatExperiment(utilityContext, experiment.experimentName))) {
				setTreated(true);
			}
		} finally {
			inFlightRef.current = false;
		}
	}, [treated, experiment, utilityContext]);

	const treatOnSlideUpOpen = useCallback(() => {
		// Fires on every open regardless of treat/dedup state, so untreated-but-exposed variant
		// traffic is identifiable and excludable at analysis (matches the pinned precedence in
		// getMotivationSuppressionReason).
		trackMotivationSuppression(track, motivationSuppressionReason, experiment.override === true);
		// Slide-up open is the treat/exposure trigger for the motivation-layer experiment (never page
		// load) -- $exposure itself fires from the effect above once treatment resolves.
		void treatOnOpen();
	}, [track, motivationSuppressionReason, experiment.override, treatOnOpen]);

	return { showMotivationCallout, treatOnSlideUpOpen };
}
